/**
 * VersionInputField — manage-deploy-versioning M4
 *
 * Reusable form section for the manage deploy dialog.
 *
 * Responsibilities:
 *  - Fetch the last SemVer git tag for the given appname (GET /v0.1/deployments/last-version)
 *  - Pre-fill the "Next version" input with the suggested minor +1 value
 *  - Validate user-typed input against SemVer (vX.Y.Z) and surface state via onValidChange
 *  - Toggle between Build / Reuse modes with appropriate label switching
 *
 * Non-goals:
 *  - This component does NOT call /trigger. The parent owns submission so we
 *    can compose multiple manage services into a single dialog if needed.
 *  - It also does not enforce uniqueness of `version` against existing tags;
 *    the workflow's `Compute next version` step rejects duplicates server-side.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
} from '@mui/material';
import deploymentService from '../../services/DeploymentService';
import type { ManageAppname } from '../../models/Deployment';

/** SemVer pattern matching backend (`triggerSemVerRe` in triggerDeploymentUseCase.go). */
const SEMVER_RE = /^v\d+\.\d+\.\d+$/;

export type DeployMode = 'build' | 'reuse';

export interface VersionInputFieldProps {
  appname: ManageAppname;
  mode: DeployMode;
  version: string; // user-controlled value (parent owns state for submit)
  onModeChange: (mode: DeployMode) => void;
  onVersionChange: (version: string) => void;
  /** Optional: parent receives validity to enable/disable submit. */
  onValidChange?: (isValid: boolean) => void;
  /** Optional: disable inputs while parent is submitting. */
  disabled?: boolean;
}

/** Human label per appname — shown in the "Current" line. */
const APPNAME_LABEL: Record<ManageAppname, string> = {
  'smartparking-manage': '관리 프론트',
  'smartparking-manage-backend': '관리 백엔드',
};

/**
 * Tracks last-fetched appname so re-renders that don't actually change the
 * appname don't re-trigger the GET (StrictMode double-mount safe).
 */
function useLastVersionFetch(appname: ManageAppname) {
  const [loading, setLoading] = useState(false);
  const [lastVersion, setLastVersion] = useState<string>('');
  const [suggestedNext, setSuggestedNext] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const lastFetchedAppname = useRef<ManageAppname | null>(null);

  useEffect(() => {
    if (lastFetchedAppname.current === appname) return;
    lastFetchedAppname.current = appname;

    let cancelled = false;
    setLoading(true);
    setError(null);
    deploymentService
      .getLastVersion(appname)
      .then((res) => {
        if (cancelled) return;
        setLastVersion(res.data?.lastVersion ?? '');
        setSuggestedNext(res.data?.suggestedNext ?? 'v0.1.0');
      })
      .catch((e) => {
        if (cancelled) return;
        // Backend already fail-soft, so this is unusual — log and fall back.
        console.warn('[VersionInputField] last-version fetch failed:', e);
        setError('마지막 버전 조회에 실패했습니다. v0.1.0 으로 진행할 수 있습니다.');
        setLastVersion('');
        setSuggestedNext('v0.1.0');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [appname]);

  return { loading, lastVersion, suggestedNext, error };
}

const VersionInputField: React.FC<VersionInputFieldProps> = ({
  appname,
  mode,
  version,
  onModeChange,
  onVersionChange,
  onValidChange,
  disabled = false,
}) => {
  const { loading, lastVersion, suggestedNext, error } = useLastVersionFetch(appname);

  // First-load auto-prefill: only set suggestedNext into the input when the
  // user hasn't typed anything yet. Subsequent fetches (appname change) do
  // overwrite, since the user is implicitly switching context.
  const userTouchedRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (mode !== 'build') return;
    if (!userTouchedRef.current) {
      onVersionChange(suggestedNext);
    }
    // We intentionally only react to suggestedNext changes — onVersionChange
    // is parent-stable and including it would cause re-prefill loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedNext, loading, mode]);

  const handleVersionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      userTouchedRef.current = true;
      onVersionChange(e.target.value);
    },
    [onVersionChange],
  );

  const isVersionValid = useMemo(() => SEMVER_RE.test(version.trim()), [version]);

  // Notify parent of validity. In reuse mode, version represents target_version
  // which has the same SemVer rule.
  useEffect(() => {
    if (!onValidChange) return;
    onValidChange(isVersionValid);
  }, [isVersionValid, onValidChange]);

  const inputLabel = mode === 'reuse' ? '재사용 버전 (Target Version)' : '다음 버전 (Next Version)';
  const inputHelper = !version
    ? 'vX.Y.Z 형식 (예: v0.2.0)'
    : isVersionValid
    ? mode === 'reuse'
      ? '이 버전 이미지를 :latest 로 재태깅하여 컨테이너를 재시작합니다.'
      : '이 버전으로 빌드 후 git tag 가 자동으로 생성됩니다.'
    : 'SemVer 형식이 아닙니다 (vX.Y.Z 가 필요).';

  return (
    <Stack spacing={1.5}>
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={(_, next) => {
          if (next && !disabled) onModeChange(next);
        }}
        size="small"
        disabled={disabled}
      >
        <ToggleButton value="build" sx={{ flex: 1 }}>Build (새 버전 빌드)</ToggleButton>
        <ToggleButton value="reuse" sx={{ flex: 1 }}>Reuse (기존 이미지 재배포)</ToggleButton>
      </ToggleButtonGroup>

      <Box>
        <Typography variant="caption" color="text.secondary" component="div">
          {APPNAME_LABEL[appname]} ({appname})
        </Typography>
        <Typography variant="body2" component="div">
          현재 운영 버전:{' '}
          {loading ? (
            <CircularProgress size={12} sx={{ ml: 0.5, verticalAlign: 'middle' }} />
          ) : lastVersion ? (
            <strong>{lastVersion}</strong>
          ) : (
            <em style={{ color: '#888' }}>(아직 배포된 버전 없음)</em>
          )}
        </Typography>
      </Box>

      <TextField
        label={inputLabel}
        value={version}
        onChange={handleVersionChange}
        placeholder="v0.2.0"
        size="small"
        fullWidth
        autoComplete="off"
        disabled={disabled || loading}
        error={Boolean(version) && !isVersionValid}
        helperText={inputHelper}
        inputProps={{ 'aria-label': inputLabel }}
      />

      {error && (
        <Alert severity="warning" variant="outlined">
          {error}
        </Alert>
      )}
    </Stack>
  );
};

export default VersionInputField;
